import { useEffect, useState } from "react";
import { ChevronLeft, FileDown, Eye } from "lucide-react";
import client, { extractError } from "@/api";

async function downloadDocument(document) {
  const response = await client.get(`/admin/commercial-invoices/${document.id}/download`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data);
  const anchor = window.document.createElement("a");
  anchor.href = url; anchor.download = document.file_name; anchor.click();
  URL.revokeObjectURL(url);
}

const Detail = ({ id, onBack }) => {
  const [document, setDocument] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    client.get(`/admin/commercial-invoices/${id}`).then((r) => setDocument(r.data))
      .catch((err) => setError(extractError(err)));
  }, [id]);
  if (error) return <div className="form-error" data-testid="admin-commercial-invoice-error">{error}</div>;
  if (!document) return <div className="loading-block">Loading…</div>;
  return (
    <div className="admin-company-detail" data-testid="admin-commercial-invoice-detail">
      <button type="button" className="link-button back" onClick={onBack} data-testid="admin-commercial-invoice-back"><ChevronLeft size={14} /> Back to uploads</button>
      <header className="detail-header">
        <div><p className="eyebrow">SELLER-UPLOADED DOCUMENT</p><h2 data-testid="admin-commercial-invoice-number">{document.invoice_number}</h2><p className="muted">{document.seller_name} → {document.buyer_name}</p></div>
        <button type="button" className="primary-button" onClick={() => downloadDocument(document)} data-testid="admin-commercial-invoice-download"><FileDown size={14} /> Download PDF</button>
      </header>
      <section className="detail-grid">
        <article><p className="eyebrow">INVOICE DATE</p><p>{new Date(`${document.invoice_date}T00:00:00`).toLocaleDateString()}</p></article>
        <article><p className="eyebrow">SUBTOTAL</p><p>₹{document.subtotal_amount.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">GST</p><p>₹{document.gst_amount.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">TOTAL</p><p><strong>₹{document.total_amount.toLocaleString("en-IN")}</strong></p></article>
        <article><p className="eyebrow">UPLOADED BY</p><p data-testid="admin-commercial-invoice-uploader">{document.uploaded_by_email || document.uploaded_by_user_id}</p></article>
        <article><p className="eyebrow">UPLOADED</p><p data-testid="admin-commercial-invoice-uploaded-at">{new Date(document.created_at).toLocaleString()}</p></article>
        {document.remarks && <article className="wide"><p className="eyebrow">REMARKS</p><p data-testid="admin-commercial-invoice-remarks">{document.remarks}</p></article>}
        <article className="wide"><p className="eyebrow">FILE</p><p>{document.file_name} · {(document.file_size / 1024).toFixed(1)} KB</p></article>
      </section>
    </div>
  );
};

export default function AdminCommercialInvoicesPage() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = async () => {
    setLoading(true); setError("");
    try { const response = await client.get("/admin/commercial-invoices?page_size=100"); setItems(response.data.items || []); }
    catch (err) { setError(extractError(err, "Unable to load uploaded commercial invoices.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (detail) return <Detail id={detail} onBack={() => { setDetail(null); load(); }} />;
  return (
    <div className="admin-listings-page" data-testid="admin-commercial-invoices-page">
      <div className="section-heading"><div><p className="eyebrow">DOCUMENTARY RECORDS</p><h3>Seller-uploaded commercial invoices</h3><p className="muted small">PDFs uploaded by sellers after trade completion. Generated Counterparty Invoices remain available under Trade Documents.</p></div></div>
      {error && <div className="form-error" data-testid="admin-commercial-invoices-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="admin-commercial-invoices-table">
          <thead><tr><th>Invoice #</th><th>Seller</th><th>Buyer</th><th>Invoice date</th><th>Subtotal</th><th>GST</th><th>Total</th><th>Action</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="empty-row" data-testid="admin-commercial-invoices-empty">No seller-uploaded commercial invoices yet.</td></tr>}
            {items.map((item) => <tr key={item.id} data-testid={`admin-commercial-invoice-row-${item.id}`}>
              <td><strong>{item.invoice_number}</strong></td><td>{item.seller_name}</td><td>{item.buyer_name}</td>
              <td>{new Date(`${item.invoice_date}T00:00:00`).toLocaleDateString()}</td>
              <td>₹{item.subtotal_amount.toLocaleString("en-IN")}</td><td>₹{item.gst_amount.toLocaleString("en-IN")}</td><td><strong>₹{item.total_amount.toLocaleString("en-IN")}</strong></td>
              <td><button type="button" className="link-button" onClick={() => setDetail(item.id)} data-testid={`admin-commercial-invoice-open-${item.id}`}><Eye size={12} /> View →</button></td>
            </tr>)}
          </tbody>
        </table>
      )}
    </div>
  );
}